import { TranscriptComponent } from '../../ConversationTranscript/types';

export interface TranscriptExchange {
  components: { component: TranscriptComponent; index: number }[];
  exchangeIndex: number;
  userQuery: string | null;
}

// The message types the audit transcript renders itself. A button selection,
// CSAT submission or quick feedback is the customer's answer, so it reads as one
// of their messages rather than as a control they operated.
const AUDIT_MESSAGE_TYPES = [
  'button_selection',
  'csat_submission',
  'embed',
  'image',
  'quick_feedback',
  'text',
];

// System markers rendered as dividers, which already print their own time.
const DIVIDER_TYPES = ['agent_testing', 'sentiment_analysis'];

export const isDivider = (component: TranscriptComponent) =>
  DIVIDER_TYPES.includes(component.message_type);

export const isAuditMessage = (component: TranscriptComponent) =>
  AUDIT_MESSAGE_TYPES.includes(component.message_type);

// The server's constant for a visitor restarting the chat
// (TranscriptBuildCommandForRestart) — recorded as an execution component even
// though it is a visitor action.
const RESTART_MESSAGE = 'Restart chat';

const isRestart = (component: TranscriptComponent) =>
  component.message_type === 'execution' &&
  (component as { message?: string }).message === RESTART_MESSAGE;

const isExchangeStart = (component: TranscriptComponent) =>
  component.component_type === 'input' || isRestart(component);

// A visitor action opens an exchange — a typed message, a button click, or a
// chat restart; everything after it attaches to the one in progress. A
// transcript opening on an agent greeting starts exchange 0 with that greeting.
export const splitIntoExchanges = (
  components: TranscriptComponent[],
): TranscriptExchange[] => {
  const exchanges: TranscriptExchange[] = [];

  components.forEach((component, index) => {
    const opensExchange = isExchangeStart(component);
    if (opensExchange || exchanges.length === 0) {
      exchanges.push({
        components: [],
        exchangeIndex: exchanges.length,
        userQuery:
          opensExchange && component.message_type === 'text'
            ? ((component as { message?: string }).message ?? null)
            : null,
      });
    }
    exchanges[exchanges.length - 1].components.push({ component, index });
  });

  return exchanges;
};

// Two recorded shapes reach the drawer: archive components carry an ISO string
// with no zone ("…T23:59:15.000000"), and error events come back off the lake as
// "… 23:59:15.000 UTC". Both are UTC, and everything downstream reads one
// zone-explicit ISO string, so they are normalised on the way in.
export const toIsoUtcTimestamp = (timestamp: string) => {
  const trimmed = timestamp.replace(' UTC', '').trim().replace(' ', 'T');
  return trimmed.endsWith('Z') ? trimmed : trimmed + 'Z';
};

export const parseComponentTimestamp = (timestamp: string) =>
  Date.parse(toIsoUtcTimestamp(timestamp));

// How long the agent took to answer: the visitor's message to the last agent
// output of the same exchange. Null for an exchange with no visitor input (the
// opening greeting) or no reply.
export const getExchangeLatencyMs = (
  exchange: TranscriptExchange,
): number | null => {
  const opening = exchange.components[0]?.component;
  if (!opening || opening.component_type !== 'input') {
    return null;
  }
  const lastOutput = [...exchange.components]
    .reverse()
    .find(({ component }) => component.component_type === 'output');
  if (!lastOutput) {
    return null;
  }
  const latency =
    parseComponentTimestamp(lastOutput.component.timestamp) -
    parseComponentTimestamp(opening.timestamp);
  return Number.isFinite(latency) && latency >= 0 ? latency : null;
};
